import { CustomOptionRequest } from "core/cart";
import { CustomOption, productAtom, ShopProduct } from "core/catalog";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { productFinalPriceAtom, selectedCustomOptionsAtom } from "../atoms";

type SelectedValues = {
  [optionId: string]: string | string[] | number | undefined;
};

const getSelectedOptionsList = (list: Map<string, CustomOptionRequest>) => {
  return Array.from(list.values());
};

/**
 * Hook to calculate and update the final price based on selected custom options
 */
export function useCalculateCustomOptionsPrice() {
  const selectedOptionsList = useRef<Map<string, CustomOptionRequest>>(
    new Map(),
  );
  const isInitialized = useRef(false);
  const product: ShopProduct = productAtom.use("product");

  const originalPrice = product.price.current || 0;

  const customOptions: CustomOption[] = useMemo(
    () => product?.customOptions || [],
    [product],
  );

  const [selectedValues, setSelectedValues] = useState<SelectedValues>({});

  // Helper function to calculate and update final price
  const calculateFinalPrice = useCallback(
    (values: SelectedValues, options: CustomOption[]) => {
      const basePrice = originalPrice;
      let totalPriceChange = 0;

      options.forEach((option) => {
        const selectedValue = values[option.id];

        if (
          selectedValue === undefined ||
          selectedValue === null ||
          selectedValue === "" ||
          (Array.isArray(selectedValue) && selectedValue.length === 0)
        )
          return;

        if (option.type === "checkbox" && selectedValue === "false") return;

        if (option.values && option.values.length > 0) {
          const valuesIds = Array.isArray(selectedValue)
            ? selectedValue
            : [selectedValue];

          valuesIds.forEach((valueId) => {
            const optionValue = option.values?.find(
              (v) => String(v.id) === String(valueId),
            );

            if (!optionValue || !optionValue.price) return;

            if (optionValue.price > 0) {
              if (optionValue.priceType === "add") {
                totalPriceChange += optionValue.price;
              } else {
                totalPriceChange -= optionValue.price;
              }
            }
          });
        } else if (option.price) {
          if (option.priceType === "add") {
            totalPriceChange += option.price;
          } else {
            totalPriceChange -= option.price;
          }
        }
      });

      const finalPrice = Math.max(0, basePrice + totalPriceChange);

      productFinalPriceAtom.update(finalPrice);
      selectedCustomOptionsAtom.update(
        getSelectedOptionsList(selectedOptionsList.current),
      );
    },
    [originalPrice],
  );

  // Initialize selected values from default values
  useEffect(() => {
    if (!isInitialized.current && customOptions.length > 0) {
      const initialValues: SelectedValues = {};

      customOptions.forEach((option) => {
        const defaultValue = option.defaultValue
          ? Array.isArray(option.defaultValue)
            ? option.defaultValue.map(String)
            : String(option.defaultValue)
          : undefined;

        initialValues[option.id] = defaultValue;

        const price = option.values
          ? option.values?.find((v) => String(v.id) === String(defaultValue))
              ?.price
          : option.price;

        // Update selectedOptionsList with initial values (include all options, even text inputs)
        // For options without predefined values (text, textarea, etc.), store the actual value
        if (defaultValue !== undefined) {
          selectedOptionsList.current.set(option.id, {
            optionId: option.id,
            value: defaultValue,
            price,
          });
        }
      });

      setSelectedValues(initialValues);
      // Calculate final price immediately after initializing values
      calculateFinalPrice(initialValues, customOptions);
      isInitialized.current = true;
    }
  }, [calculateFinalPrice, customOptions]);

  // Calculate final price whenever selected values change
  useEffect(() => {
    calculateFinalPrice(selectedValues, customOptions);
  }, [calculateFinalPrice, selectedValues, customOptions]);

  const handleOptionChange = useCallback(
    (optionId: string, value: string | string[] | number) => {
      // Update selectedOptionsList with the actual value (not just ID)
      // This works for both predefined options (select, radio) and user-entered values (text, textarea, etc.)
      const currentCustomOption = customOptions.find(
        (option) => option.id === optionId,
      );

      const price = currentCustomOption?.values
        ? currentCustomOption?.values?.find(
            (v) => String(v.id) === String(value),
          )?.price
        : currentCustomOption?.price;

      selectedOptionsList.current.set(optionId, {
        optionId,
        value,
        price,
      });
      setSelectedValues((prev) => ({
        ...prev,
        [optionId]: value,
      }));
    },
    [],
  );

  useEffect(() => {
    return () => {
      productFinalPriceAtom.reset();
    };
  }, []);

  return {
    handleOptionChange,
  };
}
